import React, { Component } from 'react'
import Modal from 'react-modal'
import { withRouter } from 'react-router-dom'
import RestApiUtils from '../../../../apiclient/moebli/RestApiUtils'
import DuplicateComplianceModalUI from './DuplicateComplianceModalUI'

/**
 * DuplicateComplianceModalSF
 *
 * Allows duplicating CP or CS documents from another loan in the same account
 * into the current loan. Fetches the list of other loans in the account on mount,
 * then on submission calls:
 *   POST /api/su/loans/{loanId}/compliances/duplicateFrom/{sourceLoanId}?type=cp|cs
 *
 * Props:
 *   context        - app context
 *   loan           - current loan entity (target)
 *   compliance     - "cp" | "cs"
 *   duplicateMode  - boolean — controls visibility
 *   onCancelClick  - function() to close the modal
 *   onRefreshClick - function(flag) to refresh the parent page
 */
class DuplicateComplianceModalSF extends Component {
  constructor(props) {
    super(props)
    this.state = {
      // Loans in same account (excluding current loan)
      accountLoans: [],
      accountLoansLoading: false,
      accountLoansErrorList: [],
      // Selected source loan
      sourceLoanId: '',
      sourceLoanIdError: null,
      // Submission
      submitted: false,
      successMsg: null,
      errorList: null,
    }
  }

  componentDidMount() {
    Modal.setAppElement('body')
  }

  componentDidUpdate(prevProps) {
    // Fetch account loans when modal opens
    if (!prevProps.duplicateMode && this.props.duplicateMode) {
      this.setState({
        sourceLoanId: '',
        sourceLoanIdError: null,
        submitted: false,
        successMsg: null,
        errorList: null,
        accountLoansErrorList: [],
      })
      this.fetchAccountLoans()
    }
  }

  fetchAccountLoans = () => {
    const { context, loan } = this.props
    this.setState({ accountLoansLoading: true, accountLoans: [] })
    RestApiUtils.get(
      context,
      `/api/su/accounts/${loan.accountId}/loans`,
      this.onFetchAccountLoansCallback,
      null, null
    )
  }

  onFetchAccountLoansCallback = (response) => {
    this.setState({ accountLoansLoading: false })
    if (response.errorList && response.errorList.length > 0) {
      this.setState({ accountLoansErrorList: response.errorList.map(e => e.message) })
      return
    }
    // Filter out the current loan — no point duplicating from itself
    const loans = (response.loanList || []).filter(
      l => l.loanId !== this.props.loan.loanId
    )
    this.setState({ accountLoans: loans, accountLoansErrorList: [] })
  }

  onSubmit = () => {
    if (this.state.submitted) return

    if (!this.state.sourceLoanId) {
      this.setState({ sourceLoanIdError: 'Please select a loan to duplicate from.' })
      return
    }

    this.setState({
      submitted: true,
      successMsg: null,
      errorList: [],
      sourceLoanIdError: null,
    })

    const { context, loan, compliance } = this.props
    const type = compliance === 'cs' ? 'cs' : 'cp'

    RestApiUtils.post(
      context,
      `/api/su/loans/${loan.loanId}/compliances/duplicateFrom/${this.state.sourceLoanId}?type=${type}`,
      this.onSubmitCallback,
      {}
    )
  }

  onSubmitCallback = (response) => {
    const errorList = response.errorList
      ? response.errorList.map(e => e.message)
      : []

    this.setState({
      submitted: false,
      successMsg: response.success,
      errorList,
    })

    if (errorList.length === 0) {
      setTimeout(() => {
        this.setState({ successMsg: null })
        this.props.onCancelClick()
        this.props.onRefreshClick('noBusy')
      }, 10)
    }
  }

  setErrorList = (errorList) => {
    this.setState({ errorList })
  }

  render() {
    const { context, loan, compliance, duplicateMode, onCancelClick } = this.props
    const {
      accountLoans, accountLoansLoading, accountLoansErrorList,
      sourceLoanId, sourceLoanIdError,
      submitted, successMsg, errorList,
    } = this.state

    // Build option list: label = "LRN00613388 - standard"
    const loanOptionList = accountLoans.map(l => ({
      key: l.loanId,
      value: `${l.loanRefNum} - ${l.loanSubType || 'unknown'}`,
    }))

    return (
      <DuplicateComplianceModalUI
        context={context}
        loan={loan}
        compliance={compliance}
        duplicateMode={duplicateMode}
        onCancelClick={onCancelClick}
        // Loans dropdown
        loanOptionList={loanOptionList}
        accountLoansLoading={accountLoansLoading}
        accountLoansErrorList={accountLoansErrorList}
        // Source loan selection
        sourceLoanId={sourceLoanId}
        onSourceLoanIdChange={(event) => {
          this.setState({ sourceLoanId: event.target.value, sourceLoanIdError: null })
        }}
        sourceLoanIdError={sourceLoanIdError}
        setSourceLoanIdError={(error) => { this.setState({ sourceLoanIdError: error }) }}
        // Submission
        submitted={submitted}
        successMsg={successMsg}
        errorList={errorList}
        setErrorList={this.setErrorList}
        onSubmit={this.onSubmit}
      />
    )
  }
}

export default withRouter(DuplicateComplianceModalSF)